import React, { useContext, useEffect, useMemo, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  MiniCart,
  AddToCart as UIFoundationsAddToCart,
  StockInfo as UIFoundationsStockInfo,
  Button as UIFoundationsButton,
  StockStatuses,
  SpacerHeights,
  Spacer,
  SpacerBorderColors,
  ButtonVariants,
  ButtonThemes,
  Dialog as UIFoundationsDialog
} from '@dcxwam/dcx-wam-ui-foundations'

import { useCookies } from 'react-cookie'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import { AddToCartModelProps, ConfiguredProduct } from './types'
import { handleAddToCart } from '../cart-management/handleAddToCart'
import { useIsSsr } from '../../../../utils/isSsr'
import { useLocalStorageCart } from '../../../../services/product/use-cart-local-storage'
import CartErrorMessageDialog from '../cart-management/CartErrorMessageDialog'
import { fetchProductData } from '../../../../services/product/productService'
import {
  createAddToCartMethodConfig,
  getAddToCartButtonLabel,
  redirectToLoginPage,
  useMiniCartConfig,
  CartProductEntry
} from '../cart-management/cartConfig'
import { getStockInfoLabel, shouldStockMessageBeShown } from '../stock-info'
import { ProductDataVariant } from '../../../../services/product/product-data'
import type { ShopConfig } from '../config'
import {
  GIGYA_COOKIE_JWT,
  GIGYA_COOKIE_UID,
  HYBRIS_ACCESS_TOKEN,
  ORDER_TYPE_ZAS,
  REPLACE_PREORDER_DATE
} from '../../../../utils/constants'
import { setMarkerProductData } from '../../../../utils/marker-product-data'
import AddToCartEditConfig from './AddToCartEditConfig'
import Container from '../../../templates/Container/Container'
import { setFormSubmitStatus } from '../../../../utils/handle-form-submit'
import { fetchContainerContent, setDialogConfig } from '../dialog-configuration'
import {
  checkIsPreorder,
  isValidDate,
  replaceDateFormat
} from '../../../../utils/preorder'
import { formatDateSimple } from '../../../../utils/date-format'
import { PageConfig, type AppConfig } from '../../../../utils/page-config'
import { UserDataContext } from '../../../../services/user/user-data'
import { createDataLayerEcommerceProduct } from '../datalayer/product'
import { getPreviewHeaders } from '../../../../utils/api-headers'
import { ProductFamilyEnum } from '../../ProductList/models'

const stockStatusMap = {
  inStock: StockStatuses.InStock,
  outOfStock: StockStatuses.OutOfStock,
  lowStock: StockStatuses.LowStock
} as const

type StockStatusKey = keyof typeof stockStatusMap

const getStockStatus = (status?: string): StockStatuses | undefined => {
  if (!status) {
    return undefined
  }
  return stockStatusMap[status as StockStatusKey]
}

const OUT_OF_STOCK_STATUS = 'outOfStock'

/**
 * Parse the authored `gcode` value (a single code or a comma-separated list)
 * into an ordered list of trimmed, non-empty product codes.
 */
const parseGcodes = (gcode?: string): string[] =>
  (gcode ?? '')
    .split(',')
    .map((code) => code.trim())
    .filter((code) => code.length > 0)

const AddToCart: React.FC<EditableComponentPropsType<AddToCartModelProps>> =
  withComponentRefresh((props) => {
    const {
      id,
      gcode,
      addToCartAriaLabel,
      addToCartLabel,
      orderType,
      outOfStockAriaLabel,
      outOfStockLabel,
      showStockLevel,
      variant,
      lendingConfigurationCode,
      appliedCssClassNames,
      ...rest
    } = props.model

    const editableProps = useExtractEditableProps(props, rest)
    const caConfig = (PageConfig?.getConfig() || {}) as AppConfig & {
      lang?: string
    }
    const { shopConfig, cbxConfig } = caConfig

    const isSsr = useIsSsr()
    const userData = useContext(UserDataContext)

    const [cartLocalStorageJSON, setCartLocalStorage] = useLocalStorageCart()
    const [isButtonDisabled, setIsButtonDisabled] = useState(true)
    const [isButtonLoading, setIsButtonLoading] = useState(false)
    const [configuredProducts, setConfiguredProducts] = useState<
      ConfiguredProduct[]
    >([])
    const [activeMiniCart, setActiveMiniCart] = useState(false)
    const [dialogNotifyMeIsOpen, setDialogNotifyMeIsOpen] = useState(false)
    const [notifyMeContent, setNotifyMeContent] = useState({})
    const [cookies, setCookie] = useCookies([
      GIGYA_COOKIE_UID,
      HYBRIS_ACCESS_TOKEN,
      GIGYA_COOKIE_JWT
    ])

    const ERROR_MESSAGE_FROM_CA = shopConfig?.addToCartErrorMessage

    // Parse the configured GCode(s). A single value keeps the legacy behavior;
    // a comma-separated list enables multi-product (bundle) support.
    const gcodes = useMemo(() => parseGcodes(gcode), [gcode])
    const isMultiProduct = gcodes.length > 1

    // Primary product drives price/preorder/mini-cart display and single-product
    // stock logic, matching the previous single-GCode behavior.
    const productData = configuredProducts[0]?.data

    // Aggregate stock: a bundle is out of stock when ANY configured product is
    // out of stock. For a single product this equals its own stock status.
    const anyProductOutOfStock = configuredProducts.some(
      (product) => product.data?.stock?.stockLevelStatus === OUT_OF_STOCK_STATUS
    )
    const aggregateStockLevelStatus =
      isMultiProduct && anyProductOutOfStock
        ? OUT_OF_STOCK_STATUS
        : productData?.stock?.stockLevelStatus

    const [dialogErrorMessageIsOpen, setDialogErrorMessageIsOpen] =
      useState(false)
    const [errorMessageContent, setErrorMessageContent] = useState({
      message: null,
      title: ERROR_MESSAGE_FROM_CA,
      actions: null
    })
    const locale =
      caConfig?.lang && caConfig?.market
        ? `${caConfig.lang}-${caConfig.market}`
        : caConfig?.lang

    const preOrderDate =
      productData?.preOrderUntilDate &&
      isValidDate(productData?.preOrderUntilDate)
        ? replaceDateFormat(productData?.preOrderUntilDate)
        : undefined

    const formattedPreorderDate = formatDateSimple(preOrderDate, locale || '')

    const preOrderMessage =
      shopConfig?.preOrderMessage?.replace(
        REPLACE_PREORDER_DATE,
        formattedPreorderDate || ''
      ) || shopConfig?.preOrderMessage

    let btnVariant = ButtonVariants.Contained
    switch (variant) {
      case 'contained':
        btnVariant = ButtonVariants.Contained
        break
      case 'outlined':
        btnVariant = ButtonVariants.Outlined
        break
      default:
        break
    }

    let btnTheme = ButtonThemes.Light
    switch (appliedCssClassNames) {
      case 'cmp-theme--light':
        btnTheme = ButtonThemes.Light
        break
      case 'cmp-theme--dark':
        btnTheme = ButtonThemes.Dark
        break
      default:
        break
    }

    const productEntry = cartLocalStorageJSON?.entry

    const isPreorder = checkIsPreorder(productData?.preOrderUntilDate)

    const fetchPData = async () => {
      if (!cbxConfig?.cbxUrl || !cbxConfig?.market || !cbxConfig?.channel) {
        return
      }
      const customHeaders = getPreviewHeaders()

      // Fetch product data for every configured code in parallel so bundle
      // stock can be aggregated. Order is preserved to keep a stable primary.
      const fetchedProducts = await Promise.all(
        gcodes.map(async (code) => {
          const data = await fetchProductData<ProductDataVariant>({
            config: cbxConfig,
            key: code,
            includeVariants: 'false',
            includeRelationships: 'false',
            includeOffline: 'false',
            includeCrosslinked: 'false',
            customHeaders
          })
          return { code, data: data ?? undefined }
        })
      )

      setConfiguredProducts(fetchedProducts)
    }

    const dataLayerEcommerceProduct =
      createDataLayerEcommerceProduct(productData)

    // Data layer product data for every configured product (bundle support).
    const dataLayerEcommerceProducts = configuredProducts.map((product) =>
      createDataLayerEcommerceProduct(product.data)
    )

    // Full product list submitted to CBX Orders in a single request. Each
    // product uses its own minimum order quantity (defaulting to 1).
    const cbxProducts = configuredProducts.map((product) => ({
      code: product.code,
      quantity: product.data?.minimumOrderQuantity || 1
    }))

    const addToCartMethodConfig = createAddToCartMethodConfig({
      orderType: orderType || '',
      lendingConfigurationCode,
      setIsButtonDisabled,
      setIsButtonLoading,
      dataLayerEcommerceProduct,
      dataLayerEcommerceProducts,
      productData: productData as ProductDataVariant,
      productID: gcodes[0] || gcode || '',
      cbxProducts,
      caConfig: {
        shopConfig,
        hybrisConfig: caConfig.hybrisConfig,
        muleSoftConfig: caConfig.muleSoftConfig,
        market: caConfig.market,
        lang: caConfig.lang,
        cbxConfig
      },
      setActiveMiniCart,
      cookies,
      setCookie: setCookie as (
        name: string,
        value: string,
        options?: {
          path?: string
          maxAge?: number
          secure?: boolean
          sameSite?: 'strict' | 'lax' | 'none'
        }
      ) => void,
      cartLocalStorageJSON,
      setCartLocalStorage,
      setErrorMessageContent: setErrorMessageContent as (content: {
        message?: string | null
        title?: string | null
        actions?: Array<{
          label?: string
          linkUrl?: string
          onClick?: () => void | Promise<void>
          buttonVariant?: unknown
        }> | null
        dismissible?: boolean
      }) => void,
      setDialogErrorMessageIsOpen,
      userData,
      productQuantity: productData?.minimumOrderQuantity || 1
    })

    const isZasOrder = orderType === ORDER_TYPE_ZAS
    const isProductSet =
      productData?.productFamily === ProductFamilyEnum.ProductSet
    const addToCartButtonLabel = getAddToCartButtonLabel({
      guestAddToCartEnabled: shopConfig?.guestAddToCartEnabled,
      preOrderUntilDate: preOrderDate,
      stockLevelStatus: isZasOrder ? undefined : aggregateStockLevelStatus,
      addToCartLabel,
      preOrderLabel: shopConfig?.preOrderCTALabel,
      notifyMe: isZasOrder ? false : shopConfig?.notifyMe,
      notifyMeCTALabel: shopConfig?.notifyMeCTALabel,
      outOfStockLabel,
      loginToBuyLabel: shopConfig?.loginToBuyLabel,
      isProductSet,
      userData,
      isMultiProduct
    })

    const onATCButtonClick = () => {
      setMarkerProductData(productData)
      if (
        addToCartButtonLabel === shopConfig?.loginToBuyLabel &&
        redirectToLoginPage(shopConfig?.loginPageURL)
      ) {
        return
      }
      if (addToCartButtonLabel === shopConfig?.notifyMeCTALabel) {
        setFormSubmitStatus('')
        setDialogNotifyMeIsOpen(true)
      }
      if (
        addToCartButtonLabel === addToCartLabel ||
        addToCartButtonLabel === shopConfig?.preOrderCTALabel
      ) {
        handleAddToCart({
          ...addToCartMethodConfig
        })
      }
    }

    const getButtonAriaLabel = (label: string | undefined) => {
      let ariaLabel = ''
      if (label === shopConfig?.loginToBuyLabel) {
        ariaLabel = ''
      } else if (label === addToCartLabel) {
        ariaLabel = addToCartAriaLabel
      } else {
        ariaLabel = outOfStockAriaLabel
      }
      return ariaLabel
    }

    const addToCartConfig = {
      buttonProps: {
        id,
        label: addToCartButtonLabel,
        loading: isButtonLoading,
        buttonVariant: btnVariant,
        buttonTheme: btnTheme,
        disable:
          (!isZasOrder && addToCartButtonLabel === outOfStockLabel) ||
          isButtonDisabled,
        onClick: onATCButtonClick
      },
      'aria-label': getButtonAriaLabel(addToCartButtonLabel),
      preOrderMessage:
        isPreorder && preOrderMessage ? preOrderMessage : undefined
    }

    const stockLabel = getStockInfoLabel({
      isSsr,
      stockLevelStatus: aggregateStockLevelStatus ?? '',
      shopConfig: shopConfig
        ? {
            inStockLabel: shopConfig.inStockLabel || '',
            outOfStockLabel: shopConfig.outOfStockLabel || '',
            lowStockLabel: shopConfig.lowStockLabel || '',
            showInStockInfoEnable: shopConfig.showInStockInfoEnable ?? false,
            attributeSelectorMarkers: shopConfig.attributeSelectorMarkers || [],
            colorGroupAriaLabel: shopConfig.colorGroupAriaLabel
          }
        : {
            inStockLabel: '',
            outOfStockLabel: '',
            lowStockLabel: '',
            showInStockInfoEnable: false,
            attributeSelectorMarkers: []
          }
    })

    const productStockStatus = getStockStatus(aggregateStockLevelStatus)

    const shouldShowStockMessage = isZasOrder
      ? false
      : shouldStockMessageBeShown({
          showStockLevel,
          shopConfigStockEnabled: shopConfig?.showInStockInfoEnable,
          productStockStatus
        })

    const stockInfoConfig = {
      showStockMessage: shouldShowStockMessage,
      stockLabel,
      stockStatus: productStockStatus
    }

    const configMiniCart = useMiniCartConfig({
      productEntry: productEntry || ({} as CartProductEntry),
      config: {
        successMessage: shopConfig?.successMessage || '',
        checkoutCTALabel: shopConfig?.checkoutCTALabel || '',
        checkoutCTAUrl: shopConfig?.checkoutCTAUrl || '',
        cartCTALabel: shopConfig?.cartCTALabel || '',
        cartCTAUrl: shopConfig?.cartCTAUrl || '',
        closeButtonAriaLabel:
          shopConfig?.closeButtonAriaLabel || shopConfig?.closeAriaLabel || '',
        showOldPrices:
          (shopConfig as ShopConfig & { showOldPrices?: boolean })
            ?.showOldPrices ?? true
      },
      variants: productData?.globalArticleCode
        ? [
            {
              globalArticleCode: productData.globalArticleCode,
              assets: productData.assets
            }
          ]
        : [],
      cartLocalStorageJSON,
      activeMiniCart,
      setActiveMiniCart,
      productQuantityLabel: shopConfig?.quantityLabel || ''
    })

    const configCartErrorMessageDialog = {
      errorMessageContent,
      dialogErrorMessageIsOpen,
      setDialogErrorMessageIsOpen
    }

    useEffect(() => {
      if (configuredProducts.length > 0) {
        setIsButtonDisabled(false)
      }
    }, [configuredProducts])

    useEffect(() => {
      if (!AuthoringUtils.isInEditor() && gcode) {
        fetchPData()
      }
    }, [])

    const configAtom = {
      label: addToCartLabel,
      buttonVariant: btnVariant,
      buttonTheme: btnTheme
    }

    const notifyMeDialog = setDialogConfig(
      dialogNotifyMeIsOpen,
      setDialogNotifyMeIsOpen,
      shopConfig?.notifyMeDialogTitle || '',
      shopConfig?.closeAriaLabel || '',
      () =>
        fetchContainerContent(shopConfig?.notifyMeXF || '', setNotifyMeContent)
    )

    useEffect(() => {
      fetchContainerContent(shopConfig?.notifyMeXF || '', setNotifyMeContent)
    }, [])

    return (
      <EditableComponent {...editableProps}>
        {AddToCartEditConfig.isEmpty(props.model)
          ? AddToCartEditConfig.emptyLabel
          : null}
        {AuthoringUtils.isInEditor() &&
        !AddToCartEditConfig.isEmpty(props.model) ? (
          <UIFoundationsButton {...configAtom} />
        ) : (
          <>
            {shouldShowStockMessage ? (
              <>
                <UIFoundationsStockInfo {...stockInfoConfig} />
                <Spacer
                  spacerHeight={SpacerHeights.Size_3}
                  spacerBorderTopTypeVariant={SpacerBorderColors.None}
                />
              </>
            ) : null}
            {gcodes.length > 0 ? (
              <UIFoundationsAddToCart {...addToCartConfig} />
            ) : null}
            <MiniCart {...configMiniCart} />
            <UIFoundationsDialog {...notifyMeDialog}>
              <Container model={{ ...notifyMeContent }} />
            </UIFoundationsDialog>
            <CartErrorMessageDialog {...configCartErrorMessageDialog} />
          </>
        )}
      </EditableComponent>
    )
  })

const AddToCartWithErrorBoundary = withErrorBoundary(AddToCart, {
  componentName: 'AddToCart',
  fallback: <ErrorFallback componentName="AddToCart" />
})

export default AddToCartWithErrorBoundary
